import React, { useMemo, useState } from "react";

import { Button, Stack, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { ValidationError } from "components/Validation-error/ValidationError";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store/selectors/user-selectors";
type LocalDateState = { from: string | null; to: string | null };

interface Props {
  initialState: { from: string; to: string };
  onSubmit: (values: { from: string; to: string }) => void;
  onCancel: () => void;
}

export const FilterDatePrompt: React.FC<Props> = ({ initialState, onSubmit, onCancel }) => {
  const dateFormat = useDateFormatSelect();

  const [localState, setLocalState] = useState<LocalDateState>(initialState);

  // Validation logic
  const validationError = useMemo(() => {
    if (!localState.from || !localState.to) return null;

    const fromDate = dayjs(localState.from, dateFormat);
    const toDate = dayjs(localState.to, dateFormat);
    const today = dayjs().startOf("day");

    if (fromDate.isBefore(today)) {
      return "From date cannot be less than today";
    }

    if (fromDate.isAfter(toDate)) {
      return "From date cannot be after to date";
    }

    return null;
  }, [localState, dateFormat]);

  const handleFromDateChange = (date: dayjs.Dayjs | null) => {
    if (date && date.isValid()) {
      setLocalState((prev) => ({ ...prev, from: date.format(dateFormat) }));
    } else {
      setLocalState((prev) => ({ ...prev, from: null }));
    }
  };

  const handleToDateChange = (date: dayjs.Dayjs | null) => {
    if (date && date.isValid()) {
      setLocalState((prev) => ({ ...prev, to: date.format(dateFormat) }));
    } else {
      setLocalState((prev) => ({ ...prev, to: null }));
    }
  };

  return (
    <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
      <Stack
        sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography sx={{ fontSize: "15px" }}>Date</Typography>
        <Stack flexDirection="row">
          <Button sx={{ color: "#26A6AE" }} variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
            variant="text"
            disabled={!!validationError || !localState.from || !localState.to}
            onClick={() => onSubmit(localState as { from: string; to: string })}
          >
            Save
          </Button>
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          value={localState.from ? dayjs(localState.from, dateFormat) : null}
          label="From"
          onChange={handleFromDateChange}
          slotProps={{
            inputAdornment: { position: "start" },
            popper: {
              sx: {
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            }
          }}
        />
        -
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          value={localState.to ? dayjs(localState.to, dateFormat) : null}
          label="To"
          onChange={handleToDateChange}
          slotProps={{
            inputAdornment: { position: "start" },
            popper: {
              sx: {
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            }
          }}
        />
      </Stack>
      {validationError && <ValidationError messages={validationError} />}
    </Stack>
  );
};
